import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

/** Max-width editorial container. */
export function Container({ id, className, children }) {
  return <div id={id} className={cn("w-full max-w-[1200px] mx-auto px-6 md:px-12", className)}>{children}</div>;
}

/** Quiet uppercase eyebrow label. */
export function SectionLabel({ children, light = false, className }) {
  return (
    <span
      className={cn(
        "block text-[11px] tracking-[0.28em] uppercase font-medium",
        light ? "text-white/80" : "text-lux-accent",
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * Flat luxury button with a hairline arrow. Renders as <Link> for internal
 * hrefs, <a> for external, and <button> when only onClick is supplied.
 */
export function ButtonLux({
  label,
  to,
  href,
  onClick,
  variant = "primary",
  light = false,
  disabled = false,
  testId,
  className,
}) {
  const base =
    "group inline-flex items-center justify-center gap-3.5 px-7 py-4 min-h-[52px] text-[12px] tracking-[0.25em] uppercase font-medium transition-all duration-300 select-none";
  const variants = {
    primary: light
      ? "bg-white text-lux-ink hover:bg-white/90"
      : "bg-lux-ink text-white hover:bg-black",
    outline: light
      ? "border border-white text-white hover:bg-white hover:text-lux-ink"
      : "border border-lux-ink text-lux-ink hover:bg-lux-ink hover:text-white",
    ghost: light ? "text-white hover:opacity-70" : "text-lux-ink hover:opacity-70",
  };
  const arrowColor =
    variant === "primary"
      ? light
        ? "bg-lux-ink group-hover:bg-white"
        : "bg-white group-hover:bg-lux-ink"
      : light
        ? "bg-white group-hover:bg-lux-ink"
        : "bg-lux-ink group-hover:bg-white";

  const inner = (
    <>
      <span>{label}</span>
      <span
        className={cn(
          "h-px w-[18px] transition-all duration-300 group-hover:w-[26px]",
          arrowColor
        )}
      />
    </>
  );
  const classes = cn(base, variants[variant], disabled && "opacity-40 pointer-events-none", className);

  if (to && !disabled) {
    return (
      <Link to={to} data-testid={testId} className={classes}>
        {inner}
      </Link>
    );
  }
  if (href && !disabled) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={classes}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={disabled ? undefined : onClick} data-testid={testId} disabled={disabled} className={classes}>
      {inner}
    </button>
  );
}

/** Fade-up reveal on scroll (IntersectionObserver, respects reduced motion). */
export function Reveal({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { setShown(true); return; }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { setShown(true); io.disconnect(); }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={cn("transition-all duration-1000 ease-out", shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5", className)}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
    >
      {children}
    </div>
  );
}
